/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef } from 'react';
import type { TFunction } from 'i18next';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS, U3203ROLES } from '../../../U3203';
import { NS as U3202_NS } from '../../../../u3202AiProvider/U3202';
import type { U3203AiMissionFormRow } from '../../../U3203Service';
import { useU3203MissionModelContext } from '../../../hooks/useU3203MissionModelContext';
import U3202ModelParameterForm from '../../../../u3202AiProvider/components/form/U3202ModelParameterForm';
import {
  u3203BuildParameterSourcesKey,
  u3203GetOverriddenKeys,
  u3203IsParameterDisplayUnseeded,
  u3203MergeDisplayValues,
  u3203ShouldSeedParameterDisplay,
} from '../../../utils/u3203MissionParameterUtils';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import {
  u2000CanEditSystemMissionOrgConfig,
  u2000CanManageScopeAwareEntity,
  u2000SessionHasOrganization,
} from '../../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3203AiMissionParametersTabProps {
  formStateHandlers: U2000FormStateHandlers<U3203AiMissionFormRow>;
  t: TFunction;
}

function U3203AiMissionParametersTab({ formStateHandlers, t }: U3203AiMissionParametersTabProps) {
  const { auth, hasRole } = useU2000AuthContext();
  const data = formStateHandlers.getData();
  const formEditMode = formStateHandlers.getFormEditMode();
  const isSystemMission = data?.systemScope === true;
  const formReadonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const canEditMissionHeader = u2000CanManageScopeAwareEntity({
    isSystemScope: isSystemMission,
    hasRequiredRole: hasRole(formEditMode === U2000FormStateEnumEditMode.ADD ? U3203ROLES.ADD : U3203ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const canEditOrgConfig = u2000CanEditSystemMissionOrgConfig({
    isSystemScope: isSystemMission,
    hasEditRole: hasRole(U3203ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const canEditOrgMission =
    !isSystemMission && hasRole(formEditMode === U2000FormStateEnumEditMode.ADD ? U3203ROLES.ADD : U3203ROLES.EDIT);
  const headerReadonly = formReadonly || (isSystemMission ? !canEditMissionHeader : !canEditOrgMission);
  const llmFieldsReadonly = isSystemMission ? formReadonly || !canEditOrgConfig : headerReadonly;
  const hideProviderModel = isSystemMission && !u2000SessionHasOrganization(auth.user.organizationId);

  const providerId = data?.defaultProviderId ?? null;
  const modelId = data?.defaultModelId ?? null;
  const { context, loading } = useU3203MissionModelContext(providerId, modelId);

  const missionId = data?.id ?? null;
  const sourcesKey = context
    ? u3203BuildParameterSourcesKey({
        missionId,
        providerId,
        modelId,
        parameterOverrides: data?.parameterOverrides,
        defaultParameters: context.defaultParameters,
      })
    : null;

  const lastSyncedSourcesKey = useRef<string | null>(null);

  useEffect(() => {
    if (!context || !sourcesKey) return;

    const current = formStateHandlers.getData();
    if (!current) return;
    if (missionId != null && current.id !== missionId) return;

    const merged = u3203MergeDisplayValues(context.defaultParameters, current.parameterOverrides ?? {});
    const displayUnseeded = u3203IsParameterDisplayUnseeded(current.parameterDisplayValues, merged);
    const alreadySynced = lastSyncedSourcesKey.current === sourcesKey;

    if (!u3203ShouldSeedParameterDisplay(alreadySynced, displayUnseeded)) return;

    lastSyncedSourcesKey.current = sourcesKey;
    formStateHandlers.setData({
      ...current,
      orgModelDefaultParameters: context.defaultParameters,
      parameterDisplayValues: merged,
    });
  }, [context, sourcesKey, formStateHandlers, missionId]);

  const overriddenKeys = useMemo(() => {
    if (!context || !data?.parameterDisplayValues) return [];
    return u3203GetOverriddenKeys(context.defaultParameters, data.parameterDisplayValues);
  }, [context, data?.parameterDisplayValues]);

  if (hideProviderModel) {
    return <p className="text-sm text-gray-600">{t('msgSystemMissionProviderModelHidden', { ns: NS })}</p>;
  }

  if (!modelId || !providerId) {
    return (
      <p className="text-sm text-gray-600">{t('msgSelectModelForParameters', { ns: NS })}</p>
    );
  }

  if (loading && !context) {
    return <p className="text-sm text-gray-600">{t('msgLoading', { ns: NS })}</p>;
  }

  if (!context) {
    return (
      <p className="text-sm text-gray-600">{t('msgSelectModelForParameters', { ns: NS })}</p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-800">{t('lblParameterOverrides', { ns: NS })}</h3>
          {overriddenKeys.length > 0 ? (
            <span className="text-xs text-amber-700">{t('lblOverriddenField', { ns: NS })}</span>
          ) : null}
        </div>
        <U3202ModelParameterForm
          formStateHandlers={formStateHandlers as any}
          parameterSchema={context.parameterSchema}
          valuesFieldPrefix="parameterDisplayValues"
          readonly={llmFieldsReadonly}
          t={t}
          ns={U3202_NS}
          overriddenKeys={overriddenKeys}
        />
      </div>
    </div>
  );
}

export default U3203AiMissionParametersTab;
